import { scheduleTracer } from './scheduling.js';
let TRACER;
export const getTracerProxy = () => {
    return TRACER;
};
export var TracerEventType;
(function (TracerEventType) {
    TracerEventType["StartUpdate"] = "StartUpdate";
    TracerEventType["EndUpdate"] = "EndUpdate";
    TracerEventType["StartLoading"] = "StartLoading";
    TracerEventType["EndLoading"] = "EndLoading";
    TracerEventType["Connected"] = "Connected";
    TracerEventType["Disconnected"] = "Disconnected";
    TracerEventType["ConsumeState"] = "ConsumeState";
})(TracerEventType || (TracerEventType = {}));
export var SignalType;
(function (SignalType) {
    SignalType["State"] = "state";
    SignalType["Reactive"] = "reactive";
    SignalType["Watcher"] = "watcher";
})(SignalType || (SignalType = {}));
export class VisualizerNode {
    tracer;
    depth;
    type;
    id;
    value;
    name;
    params;
    _setValue;
    subscribers = [];
    nextStateChildren = [];
    stateChildren = [];
    children = [];
    updating = true;
    loading = false;
    version = 0;
    updatingVersion = 0;
    didConnect = false;
    constructor(tracer, depth, type, id, value, name, params, _setValue) {
        this.tracer = tracer;
        this.depth = depth;
        this.type = type;
        this.id = id;
        this.value = value;
        this.name = name;
        this.params = params;
        this._setValue = _setValue;
        this.tracer.maxDepth = Math.max(this.tracer.maxDepth, this.depth);
    }
    get showParams() {
        return this.tracer.showParams;
    }
    get showValue() {
        return this.tracer.showValue;
    }
    get interactive() {
        return this.tracer.interactive;
    }
    setValue(value) {
        if (this.type !== SignalType.State) {
            throw new Error('setValue is only allowed on state nodes');
        }
        this._setValue?.(value);
        this.notify();
        // scheduleTracer(this.tracer);
    }
    connectChild(child) {
        let childLink = this.children.find(link => link.node.id === child.id || (link.node.name === child.name && link.version !== this.updatingVersion));
        let shouldSkip = false;
        if (childLink) {
            if (!child.didConnect) {
                child.value = childLink.node.value;
                child.children = childLink.node.children.map(link => ({
                    ...link,
                    version: child.version,
                }));
            }
            childLink.node = child;
            childLink.connected = true;
            childLink.version = this.updatingVersion;
            shouldSkip = true;
        }
        else {
            this.children.push({
                connected: true,
                node: child,
                version: this.updatingVersion,
            });
        }
        child.didConnect = true;
        this.notify();
        return shouldSkip;
    }
    disconnectChild(childId) {
        const childLink = this.children.find(link => link.node.id === childId);
        if (!childLink) {
            return;
        }
        childLink.connected = false;
        this.notify();
    }
    startUpdate() {
        this.updating = true;
        this.updatingVersion++;
        this.notify();
    }
    endUpdate(value, preserveChildren = false) {
        this.updating = false;
        this.value = value;
        if (!preserveChildren) {
            this.stateChildren = this.nextStateChildren;
        }
        this.nextStateChildren = [];
        this.notify();
    }
    startLoading() {
        this.loading = true;
        this.notify();
    }
    endLoading(value) {
        this.loading = false;
        this.value = value;
        this.notify();
    }
    consumeState(id, name, value, setValue) {
        const existing = this.stateChildren.find(child => child.id === id);
        if (existing) {
            existing.value = value;
            this.nextStateChildren.push(existing);
            existing.notify();
        }
        else {
            const node = new VisualizerNode(this.tracer, this.depth + 1, SignalType.State, id, value, name, undefined, setValue);
            node.updating = false;
            this.nextStateChildren.push(node);
        }
    }
    notify() {
        this.version++;
        for (const subscriber of this.subscribers) {
            subscriber();
        }
    }
    subscribe(subscriber) {
        this.subscribers.push(subscriber);
        return () => {
            this.subscribers = this.subscribers.filter(s => s !== subscriber);
        };
    }
}
let ID = 0;
class TraceFlush {
    forceComplete = false;
    promise;
    id = ID++;
    constructor(tracer, queue, previousFlush) {
        this.promise = this.runFlush(tracer, queue, previousFlush);
    }
    complete() {
        this.forceComplete = true;
        return this.promise;
    }
    async runFlush(tracer, queue, previousFlush) {
        if (previousFlush) {
            await previousFlush.complete();
        }
        for (let i = 0; i < queue.length; i++) {
            const event = queue[i];
            const nextEvent = queue[i + 1];
            const skipDelay = tracer.handleEvent(event, nextEvent);
            if (!this.forceComplete && !skipDelay && tracer.delay > 0 && !document.hidden) {
                await new Promise(resolve => setTimeout(resolve, tracer.delay));
            }
        }
    }
}
export class Tracer {
    showParams;
    showValue;
    interactive;
    nodeMap = new Map();
    delay = 200;
    maxDepth = 0;
    initialized = false;
    constructor(id, immediate = false, showParams = true, showValue = true, interactive = true) {
        this.showParams = showParams;
        this.showValue = showValue;
        this.interactive = interactive;
        // If it's immediate, we should run the first flush immediately, skipping animations
        this.initialized = !immediate;
        const node = new VisualizerNode(this, 0, SignalType.Watcher, id, '');
        this.rootNode = node;
        this.nodeMap.set(id, node);
    }
    rootNode;
    eventQueue = [];
    currentFlush;
    emit(event) {
        if (event.type === TracerEventType.Connected || event.type === TracerEventType.ConsumeState) {
            const node = this.nodeMap.get(event.id);
            if (!node || (event.type === TracerEventType.Connected && !event.name)) {
                return;
            }
            if (!this.nodeMap.has(event.childId)) {
                const name = event.type === TracerEventType.Connected ? event.name : undefined;
                const params = event.type === TracerEventType.Connected ? event.params : undefined;
                const nodeType = event.type === TracerEventType.Connected ? event.nodeType : SignalType.State;
                this.nodeMap.set(event.childId, new VisualizerNode(this, node.depth + 1, nodeType, event.childId, '', name, params));
            }
        }
        if (event.type === TracerEventType.StartLoading || event.type === TracerEventType.EndLoading) {
            scheduleTracer(this);
        }
        if (this.initialized) {
            this.eventQueue.push(event);
        }
        else {
            this.handleEvent(event);
        }
    }
    handleEvent(event, nextEvent) {
        const node = this.nodeMap.get(event.id);
        if (!node) {
            return true;
        }
        let skipDelay = nextEvent?.type === TracerEventType.StartLoading;
        if (event.type === TracerEventType.Connected) {
            let child = this.nodeMap.get(event.childId);
            if (!child) {
                throw new Error(`Child node ${event.childId} not found`);
            }
            skipDelay = node.connectChild(child);
        }
        else if (event.type === TracerEventType.Disconnected) {
            node.disconnectChild(event.childId);
        }
        else if (event.type === TracerEventType.StartUpdate) {
            node.startUpdate();
            if (nextEvent &&
                nextEvent.id === event.id &&
                (nextEvent.type === TracerEventType.EndUpdate || nextEvent.type === TracerEventType.StartLoading)) {
                skipDelay = true;
            }
        }
        else if (event.type === TracerEventType.EndUpdate) {
            node.endUpdate(event.value, event.preserveChildren);
        }
        else if (event.type === TracerEventType.StartLoading) {
            node.startLoading();
            skipDelay = true;
        }
        else if (event.type === TracerEventType.EndLoading) {
            node.endLoading(event.value);
        }
        else if (event.type === TracerEventType.ConsumeState) {
            node.consumeState(event.childId, event.name, event.value, event.setValue);
        }
        return skipDelay;
    }
    async flush() {
        if (this.eventQueue.length === 0) {
            return;
        }
        this.currentFlush = new TraceFlush(this, this.eventQueue, this.currentFlush);
        this.eventQueue = [];
    }
    addListener(listener) {
        this.initialized = true;
        return this.rootNode.subscribe(listener);
    }
}
class TracerProxy {
    tracers = [];
    constructor() { }
    emit(event) {
        this.tracers.forEach(tracer => tracer.emit(event));
    }
    createTracer(id, immediate = false) {
        const tracer = new Tracer(id, immediate);
        this.tracers.push(tracer);
        return tracer;
    }
    removeTracer(tracer) {
        this.tracers = this.tracers.filter(t => t !== tracer);
    }
    flush() {
        this.tracers.forEach(tracer => tracer.flush());
    }
}
export function setTracing(enabled) {
    if (enabled) {
        TRACER = new TracerProxy();
    }
    else {
        TRACER = undefined;
    }
}
export function createTracer(_signal, immediate = false) {
    const signal = _signal;
    return createTracerFromId(signal.tracerMeta.id, immediate);
}
export function createTracerFromId(id, immediate = false) {
    if (!TRACER) {
        throw new Error('Tracing is not enabled');
    }
    return TRACER.createTracer(id, immediate);
}
export function removeTracer(tracer) {
    if (!TRACER) {
        throw new Error('Tracing is not enabled');
    }
    TRACER.removeTracer(tracer);
}
